/**
 * Phase 14a-2 — admin → portal sign-in bounce.
 *
 * Builds the URL the route guard redirects to when an admin route loads
 * for an unauthenticated user. The `next` param echoes the original
 * admin destination; the portal's sign-in route validates it against
 * the open-redirect allowlist (`apps/portal/src/lib/next-redirect.ts`)
 * before honouring it post-sign-in.
 */

function envPortalUrl(): string {
  const fromVite = import.meta.env['VITE_PORTAL_URL'];
  if (typeof fromVite === 'string' && fromVite.length > 0) return fromVite;
  const fromPublic = import.meta.env['PUBLIC_PORTAL_URL'];
  if (typeof fromPublic === 'string' && fromPublic.length > 0) return fromPublic;
  // No env override → derive `app.` + this admin app's own registrable domain
  // at runtime (admin on admin.<domain> → portal app.<domain>). Zero per-env
  // config, matching the same-origin/derive approach; VITE_PORTAL_URL overrides
  // for dev tunnels (app-dev.<domain>). Without this, sign-out's bounce to the
  // portal sign-in threw and appeared broken on any env that didn't set the var.
  if (typeof window !== 'undefined') {
    const host = window.location.host.split(':')[0] ?? '';
    const labels = host.split('.');
    if (labels.length >= 2) {
      const base = labels.slice(-2).join('.'); // registrable domain (our TLDs are 2-label)
      return `${window.location.protocol}//app.${base}`;
    }
  }
  // Last resort (SSR / bare non-domain host with no override) — fail loud.
  throw new Error(
    'VITE_PORTAL_URL is not configured and the portal URL could not be derived from the host.',
  );
}

function envAdminUrl(): string {
  const fromVite = import.meta.env['VITE_ADMIN_URL'];
  if (typeof fromVite === 'string' && fromVite.length > 0) return fromVite;
  const fromPublic = import.meta.env['PUBLIC_ADMIN_URL'];
  if (typeof fromPublic === 'string' && fromPublic.length > 0) return fromPublic;
  // Falls back to window.location.origin so dev still works without
  // explicit env vars (the operator's current admin origin is the
  // legitimate next URL in that case).
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}

/**
 * Constructs the portal sign-in URL with `?next=<encoded admin URL>`
 * pointing back at the admin route the user originally tried to load.
 *
 * @param currentPath admin in-app path (e.g. '/dashboard'); defaults to
 *   `window.location.pathname + window.location.search` so route guards
 *   round-trip the full URL the operator landed on.
 * @param portalPath portal route to land on — '/sign-in' (default, no session)
 *   or '/sign-in/totp' (session exists but TOTP not yet satisfied). Both honour
 *   the `?next` round-trip.
 */
export function buildSignInRedirect(
  currentPath?: string,
  portalPath: '/sign-in' | '/sign-in/totp' = '/sign-in',
): string {
  const portal = envPortalUrl();
  const admin = envAdminUrl();
  const path =
    currentPath ??
    (typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : '/');
  const next = new URL(path, admin).toString();
  const signInUrl = new URL(portalPath, portal);
  signInUrl.searchParams.set('next', next);
  return signInUrl.toString();
}
